import {
    REGULATIONS_CACHE_KEY,
    clearRegulationsCache,
} from '@/utils/regulations-cache'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { getActiveRegulation, getMyRegulationStatus } from '../services'
import { MyRegulationStatus, RegulationsConfig } from '../types'
import useAuthStore from '@/stores/useAuthStore'
import { useEffect, useMemo } from 'react'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'

interface RegulationsCacheData {
    employeeId: number
    regulationVersion: string // Current active version when cached
    acceptedVersion: string
    timestamp: number
}

// Cache valid for 4 hours
// Trade-off: Shorter = more API calls but fresher data (catches new regulations faster)
// Longer = fewer API calls but may miss new regulations within cache window
const CACHE_DURATION = 4 * 60 * 60 * 1000

const getCache = (): RegulationsCacheData | null => {
    try {
        const cached = localStorage.getItem(REGULATIONS_CACHE_KEY)
        if (!cached) return null

        const data: RegulationsCacheData = JSON.parse(cached)
        // Check if cache is expired
        if (Date.now() - data.timestamp > CACHE_DURATION) {
            localStorage.removeItem(REGULATIONS_CACHE_KEY)
            return null
        }
        return data
    } catch {
        return null
    }
}

const setCache = (
    employeeId: number,
    regulationVersion: string,
    acceptedVersion: string
) => {
    try {
        const data: RegulationsCacheData = {
            employeeId,
            regulationVersion,
            acceptedVersion,
            timestamp: Date.now(),
        }
        localStorage.setItem(REGULATIONS_CACHE_KEY, JSON.stringify(data))
    } catch {
        // Ignore storage errors
    }
}

interface UseRegulationsCheckResult {
    shouldShowRegulations: boolean
    regulationData: RegulationsConfig | null
    myStatus: MyRegulationStatus | null
    isLoading: boolean
}

export const useRegulationsCheck = (): UseRegulationsCheckResult => {
    const employee = useAuthStore((state) => state?.employee)

    // Check if we have valid cache for this employee
    const cachedData = useMemo(() => {
        if (!employee?.employee_id) return null
        const cache = getCache()
        if (cache && cache.employeeId === employee.employee_id) {
            return cache
        }
        return null
    }, [employee?.employee_id])

    // Skip API call if we have valid cache (already accepted)
    const shouldSkipStatusCheck = !!cachedData

    // Get my regulation status (includes needs_acceptance flag and current_active_version)
    const { data: statusResponse, renderState: statusRenderState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.GENERIC, 'regulations-my-status'],
            queryFn: getMyRegulationStatus,
            enabled: !!employee && !shouldSkipStatusCheck,
        })

    const myStatus = statusResponse?.result ?? null

    // Validate cache against current regulation version when API is called
    const isCacheValidForCurrentRegulation = useMemo(() => {
        if (!cachedData || !myStatus) return false
        // Cache is only valid if regulation version matches current active version
        return cachedData.regulationVersion === myStatus.current_active_version
    }, [cachedData, myStatus?.current_active_version])

    // Cache the result when needs_acceptance is false
    useEffect(() => {
        if (
            employee?.employee_id &&
            myStatus &&
            !myStatus.needs_acceptance &&
            myStatus.accepted_version &&
            myStatus.current_active_version
        ) {
            // Store with current regulation version for future validation
            setCache(
                employee.employee_id,
                myStatus.current_active_version,
                myStatus.accepted_version
            )
        }
    }, [employee?.employee_id, myStatus])

    // Clear cache if regulation version changed (new regulation published)
    useEffect(() => {
        if (myStatus && cachedData && !isCacheValidForCurrentRegulation) {
            // Regulation version changed, clear old cache
            clearRegulationsCache()
        }
    }, [myStatus, cachedData, isCacheValidForCurrentRegulation])

    // Determine if we need to show regulations
    const needsAcceptance = useMemo(() => {
        // If we have API response, use it as source of truth
        if (myStatus) {
            return myStatus.needs_acceptance
        }
        // If skipped API due to cache, assume no acceptance needed
        if (shouldSkipStatusCheck) {
            return false
        }
        return false
    }, [myStatus, shouldSkipStatusCheck])

    // Get active regulation config with attachments (for displaying content)
    const { data: configResponse, renderState: configRenderState } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.GENERIC, 'regulations-config'],
            queryFn: getActiveRegulation,
            enabled: !!employee && needsAcceptance,
            retry: false,
        })

    const regulationData = configResponse ?? null

    return {
        shouldShowRegulations: needsAcceptance,
        regulationData,
        myStatus,
        isLoading:
            (!shouldSkipStatusCheck && !!statusRenderState()) ||
            (needsAcceptance && !!configRenderState()),
    }
}

export { clearRegulationsCache }
